// Research + sender-account API client, and the position-list parser.

import { outreachFetch } from "./business";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await outreachFetch(`/api/outreach${path}`, {
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
    ...init,
  });
  if (res.status === 401) { window.location.href = "/login"; throw new Error("Unauthorized"); }
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error((e as { error?: string }).error || `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export interface PositionGroup { name: string; roles: string[] }

export interface GroupState extends PositionGroup {
  status: "pending" | "running" | "done" | "error";
  error: string | null;
  found: number;
  finished_at: string | null;
  cost_usd?: number;
  mode?: "fast" | "thorough";
}

export interface RunSummary {
  id: number; company: string; country: string | null; status: string;
  cost_usd: number; web_searches: number; people: number; added: number;
  created_by_email: string | null; created_at: string; finished_at: string | null;
}

export interface Run extends Omit<RunSummary, "people" | "added"> {
  domain: string | null;
  email_domain: string | null;
  email_pattern: string | null;
  groups: GroupState[];
  evidence: { domain: string | null; pattern: string | null; matches: number; domain_total: number; sample: string[]; pool?: number; same_country?: boolean } | null;
}

export interface Source { url: string; title: string; seen: boolean }

export interface ResearchResult {
  id: number; run_id: number; group_name: string | null;
  full_name: string; current_title: string | null;
  matched_roles: string | null; confidence: "high" | "medium" | "low" | null;
  evidence_date: string | null; sources_json: string | null;
  public_email: string | null; public_email_source: string | null;
  email: string | null; email_basis: "public" | "pattern" | "domain-only" | "manual" | "none" | null;
  alt_emails: string | null; notes: string | null;
  assignments: { business_id: number; contact_id: number; email: string | null; verification_status: string }[];
}

export interface Sender {
  id: number; business_id: number; label: string; from_name: string | null; from_email: string; reply_to: string | null;
  key_last4: string | null; has_webhook_secret: boolean; disabled: boolean;
  created_by_email: string | null; created_at: string; updated_at: string;
}

export interface PositionList { id: number; name: string; content: string; created_by_email: string | null; created_at: string }

export const research = {
  lists: () => request<{ lists: PositionList[] }>("/research/lists"),
  saveList: (name: string, content: string) =>
    request<{ id: number }>("/research/lists", { method: "POST", body: JSON.stringify({ name, content }) }),
  updateList: (id: number, body: { name?: string; content?: string }) =>
    request<{ ok: true }>(`/research/lists/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteList: (id: number) => request<{ ok: true }>(`/research/lists/${id}`, { method: "DELETE" }),

  runs: () => request<{ runs: RunSummary[] }>("/research/runs"),
  createRun: (body: { company: string; country?: string; domain?: string; groups: PositionGroup[]; mode: "fast" | "thorough" }) =>
    request<{ id: number }>("/research/runs", { method: "POST", body: JSON.stringify(body) }),
  run: (id: number) => request<{ run: Run; results: ResearchResult[] }>(`/research/runs/${id}`),
  deleteRun: (id: number) => request<{ ok: true }>(`/research/runs/${id}`, { method: "DELETE" }),
  assign: (runId: number, resultIds: number[], businessId: number) =>
    request<{ ok: true; contacts: { result_id: number; contact_id: number; created: boolean }[] }>(
      `/research/runs/${runId}/add-contacts`, { method: "POST", body: JSON.stringify({ result_ids: resultIds, business_id: businessId }) },
    ),
  setEmail: (resultId: number, email: string) =>
    request<{ ok: true }>(`/research/results/${resultId}`, { method: "PATCH", body: JSON.stringify({ email }) }),
  deleteResult: (resultId: number) => request<{ ok: true }>(`/research/results/${resultId}`, { method: "DELETE" }),

  senders: () => request<{ senders: Sender[] }>("/senders"),
  createSender: (body: Partial<Sender> & { business_id: number; resend_api_key: string; webhook_secret?: string }) =>
    request<{ id: number; warning: string | null }>("/senders", { method: "POST", body: JSON.stringify(body) }),
  updateSender: (id: number, body: Partial<Sender> & { resend_api_key?: string; webhook_secret?: string }) =>
    request<{ ok: true; warning: string | null }>(`/senders/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  disableSender: (id: number) => request<{ ok: true }>(`/senders/${id}`, { method: "DELETE" }),
};

export type GroupEvent =
  | { type: "start"; group: string }
  | { type: "ping"; at: number }
  | { type: "done"; found: number; not_found_roles: string[]; usage: { cost_usd: number; web_searches: number } }
  | { type: "error"; error: string };

/** Run one group; resolves with the final event. The server streams NDJSON with heartbeats. */
export async function runGroup(runId: number, index: number, force = false, onEvent?: (e: GroupEvent) => void): Promise<GroupEvent> {
  const res = await outreachFetch(`/api/outreach/research/runs/${runId}/group`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ index, force }),
  });
  if (!res.ok || !res.body) {
    const e = await res.json().catch(() => ({}));
    return { type: "error", error: (e as { error?: string }).error || `HTTP ${res.status}` };
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let last: GroupEvent = { type: "error", error: "Connection closed before the group finished — reload to see whether it completed" };
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      try {
        const ev = JSON.parse(line) as GroupEvent;
        onEvent?.(ev);
        if (ev.type === "done" || ev.type === "error") last = ev;
      } catch { /* partial or junk line */ }
    }
  }
  return last;
}

/**
 * Parse a pasted/uploaded list of positions into groups.
 *   • Group heading        (bullet •, #, or a line ending in ":")
 *     o Position           (o, -, *, –, ·, 1. or indented)
 * A list with no headings is split into groups of 6 so each search stays focused.
 */
export function parsePositions(text: string): PositionGroup[] {
  const groups: PositionGroup[] = [];
  let current: PositionGroup | null = null;
  const loose: string[] = [];

  for (const rawLine of text.split(/\r?\n/)) {
    if (!rawLine.trim()) continue;
    const indented = /^[\t ]{2,}|^\t/.test(rawLine);
    const line = rawLine.trim();

    const heading = /^[•#]+\s*(.+)$/.exec(line) || (!indented && /^(.+):$/.exec(line));
    if (heading) {
      current = { name: heading[1].replace(/:$/, "").trim(), roles: [] };
      groups.push(current);
      continue;
    }
    const item = /^(?:o|-|\*|–|·|▪|◦|\d+[.)])\s+(.+)$/.exec(line);
    const role = (item ? item[1] : line).trim();
    if (!role) continue;
    if (current && (item || indented)) current.roles.push(role);
    else if (current) current.roles.push(role);
    else loose.push(role);
  }

  const chunks: PositionGroup[] = [];
  for (let i = 0; i < loose.length; i += 6) {
    chunks.push({ name: `Positions ${i + 1}–${Math.min(i + 6, loose.length)}`, roles: loose.slice(i, i + 6) });
  }
  groups.unshift(...chunks);
  return groups.filter(g => g.roles.length);
}

/** Rows from a spreadsheet: [group, position] pairs, or a single column of positions. */
export function positionsFromRows(rows: unknown[][]): string {
  const clean = rows.map(r => r.map(c => String(c ?? "").trim())).filter(r => r.some(Boolean));
  if (!clean.length) return "";
  const twoCol = clean.filter(r => r[0] && r[1]).length >= clean.length / 2;
  if (!twoCol) return clean.map(r => r.find(Boolean)!).join("\n");
  const out: string[] = [];
  let last = "";
  for (const r of clean) {
    const g = r[0] || last;
    if (g !== last) { out.push(`• ${g}`); last = g; }
    if (r[1]) out.push(`  o ${r[1]}`);
  }
  return out.join("\n");
}

export const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Argentina","Armenia","Australia","Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan","Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cambodia","Cameroon","Canada","Cape Verde","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica","Côte d'Ivoire","Croatia","Cuba","Cyprus","Czech Republic","Democratic Republic of the Congo","Denmark","Djibouti","Dominican Republic","Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana","Greece","Guatemala","Guinea","Guyana","Haiti","Honduras","Hong Kong","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kosovo","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Macau","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Mauritania","Mauritius","Mexico","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Macedonia","Norway","Oman","Pakistan","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Puerto Rico","Qatar","Romania","Russia","Rwanda","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Somalia","South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe",
];
